import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { marketingSocialMentionEntity } from 'mj_generatedentities';
import { runAction } from '../../shared/run-action.util.js';
import { RoleTier } from '../../role-filter';

type EntityType = 'Person' | 'Organization';
type MentionStatus = 'Active' | 'Inactive';

interface MentionForm {
    EntityType: EntityType;
    DisplayName: string;
    LocalizedName: string;
    LinkedInURN: string;
    VanityName: string;
    ProfileURL: string;
    Status: MentionStatus;
}

/**
 * Read-only projection of the fields the list/editor reads. The grid never
 * mutates these rows — saves/deletes go through Marketing actions — so we load
 * them with ResultType 'simple' and skip per-row BaseEntity construction.
 */
type MentionRow = Pick<
    marketingSocialMentionEntity,
    'ID' | 'EntityType' | 'DisplayName' | 'LocalizedName' | 'LinkedInURN' | 'VanityName' | 'ProfileURL' | 'Status'
>;

function emptyForm(): MentionForm {
    return {
        EntityType: 'Organization',
        DisplayName: '',
        LocalizedName: '',
        LinkedInURN: '',
        VanityName: '',
        ProfileURL: '',
        Status: 'Active',
    };
}

/**
 * Foundation · Mention directory — per-company directory of taggable LinkedIn
 * entities (people / organizations). Rows here become selectable in the social
 * post editor's @-mention picker; the backend resolves the inline
 * `@[text](mentionId)` tokens at publish.
 *
 * Reads load directly via RunView (simple — the grid only displays rows).
 * Saves/deletes route through the `Save Social Mention` / `Delete Social Mention`
 * Marketing actions so the write path stays server-validated.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-mention-directory',
    template: `
<mkt-ws-page-head
    eyebrow="Foundation · Mention directory"
    title="LinkedIn @-mentions."
    sub="Per-company directory of people and organizations you can tag in social posts. Editors pick from these in the post composer; the publisher resolves them to real LinkedIn annotations.">
</mkt-ws-page-head>

<div class="md__bar">
    <div class="md__bar-info">
        <span class="md__count">{{ mentions.length }} mention{{ mentions.length !== 1 ? 's' : '' }}</span>
        <span class="md__count md__count--muted" *ngIf="mentions.length">· {{ activeCount }} active</span>
    </div>
    <button class="md__btn md__btn--primary" (click)="startAdd()" [disabled]="!companyId || editing">
        <i class="fa-solid fa-at"></i> Add mention
    </button>
</div>

<!-- Editor form (add / edit) -->
<div class="md__form" *ngIf="editing">
    <div class="md__form-head">
        <span class="md__form-title">{{ editingId ? 'Edit mention' : 'New mention' }}</span>
    </div>
    <div class="md__grid">
        <label class="md__field">
            <span class="md__label">Type <span class="md__req">*</span></span>
            <select class="md__input" [(ngModel)]="form.EntityType" [disabled]="saving">
                <option value="Organization">Organization</option>
                <option value="Person">Person</option>
            </select>
        </label>
        <label class="md__field">
            <span class="md__label">Status</span>
            <select class="md__input" [(ngModel)]="form.Status" [disabled]="saving">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </label>
        <label class="md__field">
            <span class="md__label">Display name <span class="md__req">*</span></span>
            <input class="md__input" [(ngModel)]="form.DisplayName" [disabled]="saving"
                   placeholder='Searchable name, e.g. "Buffer" or "Jane Doe"' />
        </label>
        <label class="md__field">
            <span class="md__label">Localized name <span class="md__req">*</span></span>
            <input class="md__input" [(ngModel)]="form.LocalizedName" [disabled]="saving"
                   placeholder='Text rendered in the post, e.g. "Buffer"' />
        </label>
        <label class="md__field md__field--wide">
            <span class="md__label">LinkedIn URN <span class="md__req">*</span></span>
            <input class="md__input" [(ngModel)]="form.LinkedInURN" [disabled]="saving"
                   placeholder="urn:li:organization:1521226 or urn:li:person:xxxx" />
        </label>
        <label class="md__field">
            <span class="md__label">Vanity name</span>
            <input class="md__input" [(ngModel)]="form.VanityName" [disabled]="saving"
                   placeholder='Slug after /company/ or /in/, e.g. "bufferapp"' />
        </label>
        <label class="md__field">
            <span class="md__label">Profile URL</span>
            <input class="md__input" [(ngModel)]="form.ProfileURL" [disabled]="saving"
                   placeholder="https://www.linkedin.com/company/bufferapp" />
        </label>
    </div>

    <div class="md__form-error" *ngIf="formError">
        <i class="fa-solid fa-circle-exclamation"></i> {{ formError }}
    </div>

    <div class="md__form-actions">
        <button class="md__btn" (click)="cancelEdit()" [disabled]="saving">Cancel</button>
        <button class="md__btn md__btn--primary" (click)="save()" [disabled]="saving">
            <i class="fa-solid" [class.fa-spinner]="saving" [class.fa-spin]="saving" [class.fa-check]="!saving"></i>
            {{ saving ? 'Saving…' : 'Save mention' }}
        </button>
    </div>
</div>

<!-- List -->
<div class="md__list" *ngIf="!loading">
    <div class="md__empty" *ngIf="mentions.length === 0">
        <i class="fa-solid fa-at"></i>
        <p>No mentions yet for this company. Add one to make it taggable in social posts.</p>
    </div>

    <table class="md__table" *ngIf="mentions.length > 0">
        <thead>
            <tr>
                <th>Display name</th>
                <th>Type</th>
                <th>Localized name</th>
                <th>LinkedIn URN</th>
                <th>Status</th>
                <th class="md__th-actions"></th>
            </tr>
        </thead>
        <tbody>
            <tr *ngFor="let m of mentions" [class.md__row--inactive]="m.Status === 'Inactive'">
                <td class="md__td-name">{{ m.DisplayName }}</td>
                <td>
                    <span class="md__type">
                        <i class="fa-solid" [class.fa-user]="m.EntityType === 'Person'" [class.fa-building]="m.EntityType === 'Organization'"></i>
                        {{ m.EntityType }}
                    </span>
                </td>
                <td>{{ m.LocalizedName }}</td>
                <td class="md__td-urn">{{ m.LinkedInURN }}</td>
                <td>
                    <span class="md__status" [class.md__status--active]="m.Status === 'Active'">{{ m.Status }}</span>
                </td>
                <td class="md__td-actions">
                    <button class="md__icon-btn" (click)="startEdit(m)" [disabled]="editing" title="Edit">
                        <i class="fa-solid fa-pen"></i>
                    </button>
                    <button class="md__icon-btn md__icon-btn--danger" (click)="remove(m)" [disabled]="editing || rowBusyId === m.ID" title="Delete">
                        <i class="fa-solid" [class.fa-spinner]="rowBusyId === m.ID" [class.fa-spin]="rowBusyId === m.ID" [class.fa-trash]="rowBusyId !== m.ID"></i>
                    </button>
                </td>
            </tr>
        </tbody>
    </table>

    <div class="md__list-error" *ngIf="listError">
        <i class="fa-solid fa-circle-exclamation"></i> {{ listError }}
    </div>
</div>

<div class="md__loading" *ngIf="loading">
    <i class="fa-solid fa-spinner fa-spin"></i> Loading mentions…
</div>
    `,
    styles: [`
        :host { display: block; }

        .md__bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
        .md__bar-info { display: flex; align-items: baseline; gap: 8px; }
        .md__count { font-size: 13px; color: var(--mj-text-primary, #1a1a1a); font-weight: 500; }
        .md__count--muted { color: var(--mj-text-secondary, #71717a); font-weight: 400; }

        .md__btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); font-size: 13px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); cursor: pointer; font-family: inherit; }
        .md__btn:hover:not(:disabled) { background: var(--mj-bg-page, #f5f5f5); }
        .md__btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .md__btn--primary { background: var(--mj-brand-primary, #6d28d9); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6d28d9); }
        .md__btn--primary:hover:not(:disabled) { filter: brightness(1.1); }

        .md__form { border: 1px solid var(--mj-brand-primary, #6d28d9); border-radius: 10px; padding: 18px 20px; margin-bottom: 18px; background: var(--mj-bg-surface, #fff); }
        .md__form-head { margin-bottom: 14px; }
        .md__form-title { font-size: 14px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); }
        .md__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
        @media (max-width: 700px) { .md__grid { grid-template-columns: 1fr; } }
        .md__field { display: flex; flex-direction: column; gap: 5px; }
        .md__field--wide { grid-column: 1 / -1; }
        .md__label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: var(--mj-text-secondary, #71717a); }
        .md__req { color: var(--mj-status-error, #dc2626); }
        .md__input { width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; font-size: 13px; font-family: inherit; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); }
        .md__input:focus { border-color: var(--mj-brand-primary, #6d28d9); outline: none; }
        .md__input:disabled { background: var(--mj-bg-page, #f5f5f5); color: var(--mj-text-secondary, #71717a); }

        .md__form-error, .md__list-error { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 9px 12px; border-radius: 6px; background: var(--mj-status-error-bg, #fef2f2); color: var(--mj-status-error, #dc2626); font-size: 12.5px; }
        .md__form-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }

        .md__list { background: var(--mj-bg-surface, #fff); }
        .md__empty { text-align: center; padding: 40px 20px; color: var(--mj-text-secondary, #71717a); border: 1px dashed var(--mj-border-default, #e0e0e0); border-radius: 10px; }
        .md__empty i { font-size: 24px; display: block; margin-bottom: 8px; }

        .md__table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .md__table th { text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--mj-text-secondary, #71717a); font-weight: 600; padding: 8px 12px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .md__table td { padding: 10px 12px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); color: var(--mj-text-primary, #1a1a1a); vertical-align: middle; }
        .md__row--inactive td { opacity: 0.55; }
        .md__td-name { font-weight: 500; }
        .md__td-urn { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .md__type { display: inline-flex; align-items: center; gap: 6px; }
        .md__type i { color: var(--mj-text-secondary, #71717a); width: 14px; }
        .md__status { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: var(--mj-bg-page, #f5f5f5); color: var(--mj-text-secondary, #71717a); }
        .md__status--active { background: var(--mj-status-success-bg, #f0fdf4); color: var(--mj-color-success-500, #16a34a); }
        .md__th-actions { width: 80px; }
        .md__td-actions { text-align: right; white-space: nowrap; }
        .md__icon-btn { background: none; border: none; cursor: pointer; padding: 5px 7px; border-radius: 5px; color: var(--mj-text-secondary, #71717a); }
        .md__icon-btn:hover:not(:disabled) { background: var(--mj-bg-page, #f5f5f5); color: var(--mj-text-primary, #1a1a1a); }
        .md__icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .md__icon-btn--danger:hover:not(:disabled) { color: var(--mj-status-error, #dc2626); background: var(--mj-status-error-bg, #fef2f2); }

        .md__loading { padding: 32px; text-align: center; color: var(--mj-text-secondary, #71717a); font-size: 13px; }
    `]
})
export class MentionDirectorySection implements OnChanges {
    private cdr = inject(ChangeDetectorRef);

    private _companyId: string | null = null;
    @Input() set companyId(v: string | null) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.loadMentions();
        } else if (!v) {
            this._companyId = null;
            this.mentions = [];
        }
    }
    get companyId(): string | null { return this._companyId; }

    @Input() userRole: RoleTier | null = null;

    mentions: MentionRow[] = [];
    loading = false;
    listError = '';

    editing = false;
    editingId: string | null = null;
    saving = false;
    formError = '';
    form: MentionForm = emptyForm();

    rowBusyId: string | null = null;

    ngOnChanges(_changes: SimpleChanges): void {
        // companyId handled by setter; nothing else reactive.
    }

    get activeCount(): number {
        return this.mentions.filter(m => m.Status === 'Active').length;
    }

    private async loadMentions(): Promise<void> {
        if (!this.companyId) return;
        this.loading = true;
        this.listError = '';
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const result = await rv.RunView<MentionRow>({
                EntityName: 'Social Mentions',
                ExtraFilter: `CompanyID='${this.companyId}'`,
                OrderBy: 'DisplayName ASC',
                ResultType: 'simple',
                MaxRows: 1000,
            });
            if (result.Success) {
                this.mentions = result.Results;
            } else {
                this.listError = result.ErrorMessage || 'Failed to load mentions.';
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MentionDirectory] loadMentions failed: ${msg}`);
            this.listError = msg;
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    startAdd(): void {
        this.editingId = null;
        this.form = emptyForm();
        this.formError = '';
        this.editing = true;
    }

    startEdit(m: MentionRow): void {
        this.editingId = m.ID;
        this.form = {
            EntityType: m.EntityType,
            DisplayName: m.DisplayName,
            LocalizedName: m.LocalizedName,
            LinkedInURN: m.LinkedInURN,
            VanityName: m.VanityName ?? '',
            ProfileURL: m.ProfileURL ?? '',
            Status: m.Status,
        };
        this.formError = '';
        this.editing = true;
    }

    cancelEdit(): void {
        this.editing = false;
        this.editingId = null;
        this.formError = '';
    }

    private validate(): string | null {
        if (!this.companyId) return 'No company selected.';
        if (!this.form.EntityType) return 'Type is required.';
        if (!this.form.DisplayName.trim()) return 'Display name is required.';
        if (!this.form.LocalizedName.trim()) return 'Localized name is required.';
        if (!this.form.LinkedInURN.trim()) return 'LinkedIn URN is required.';
        return null;
    }

    async save(): Promise<void> {
        const validationError = this.validate();
        if (validationError) {
            this.formError = validationError;
            return;
        }
        this.saving = true;
        this.formError = '';
        this.cdr.detectChanges();
        try {
            const params: { Name: string; Value: string }[] = [
                { Name: 'CompanyID', Value: this.companyId! },
                { Name: 'EntityType', Value: this.form.EntityType },
                { Name: 'DisplayName', Value: this.form.DisplayName.trim() },
                { Name: 'LocalizedName', Value: this.form.LocalizedName.trim() },
                { Name: 'LinkedInURN', Value: this.form.LinkedInURN.trim() },
                { Name: 'Status', Value: this.form.Status },
            ];
            if (this.editingId) params.push({ Name: 'ID', Value: this.editingId });
            if (this.form.VanityName.trim()) params.push({ Name: 'VanityName', Value: this.form.VanityName.trim() });
            if (this.form.ProfileURL.trim()) params.push({ Name: 'ProfileURL', Value: this.form.ProfileURL.trim() });

            const r = await runAction('Save Social Mention', params);
            if (!r.success) {
                this.formError = `Save failed: ${r.message}`;
                return;
            }
            this.editing = false;
            this.editingId = null;
            await this.loadMentions();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MentionDirectory] save failed: ${msg}`);
            this.formError = msg;
        } finally {
            this.saving = false;
            this.cdr.detectChanges();
        }
    }

    async remove(m: MentionRow): Promise<void> {
        if (!this.companyId) return;
        this.rowBusyId = m.ID;
        this.listError = '';
        this.cdr.detectChanges();
        try {
            const r = await runAction('Delete Social Mention', [
                { Name: 'CompanyID', Value: this.companyId },
                { Name: 'ID', Value: m.ID },
            ]);
            if (!r.success) {
                this.listError = `Delete failed: ${r.message}`;
                return;
            }
            await this.loadMentions();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[MentionDirectory] remove failed: ${msg}`);
            this.listError = msg;
        } finally {
            this.rowBusyId = null;
            this.cdr.detectChanges();
        }
    }
}
